import { useId, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { type CronFieldKey, explainCron } from "../lib/cron";

const EXAMPLES = [
  "*/5 * * * *",
  "0 * * * *",
  "0 0 * * *",
  "0 9 * * 1-5",
  "*/15 9-17 * * *",
  "0 0 1 * *",
];

const FIELD_LABELS: Array<[CronFieldKey, () => string]> = [
  ["minute", () => m.cr_f_minute()],
  ["hour", () => m.cr_f_hour()],
  ["dom", () => m.cr_f_dom()],
  ["month", () => m.cr_f_month()],
  ["dow", () => m.cr_f_dow()],
];

export function CronTool() {
  const uid = useId();
  const [expr, setExpr] = useState("*/5 * * * *");
  const result = useMemo(() => explainCron(expr), [expr]);

  return (
    <div className="space-y-6">
      <div className="space-y-1.5 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <Label htmlFor={`${uid}-cron`}>{m.cr_input_label()}</Label>
        <Input
          id={`${uid}-cron`}
          type="text"
          value={expr}
          onChange={(e) => setExpr(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          className="font-mono text-lg"
        />

        <div className="pt-4">
          {result.ok ? (
            <p
              data-testid="cron-text"
              className="font-display text-xl font-semibold text-foreground"
            >
              {result.text}
            </p>
          ) : (
            <p data-testid="cron-error" className="text-sm text-destructive">
              {result.error}
            </p>
          )}
        </div>
      </div>

      {result.ok && (
        <div className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {m.cr_fields_title()}
          </h2>
          <dl className="grid gap-3 sm:grid-cols-2">
            {FIELD_LABELS.map(([key, label]) => (
              <div
                key={key}
                className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-2"
              >
                <dt className="text-sm text-muted-foreground">{label()}</dt>
                <dd className="text-right font-medium text-foreground">
                  {result.fields[key]}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {m.cr_examples_title()}
        </h2>
        <div className="flex flex-wrap gap-2">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => setExpr(ex)}
              className="rounded-md border border-border px-3 py-1.5 font-mono text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {ex}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
